// Tool / snap / axis-lock / space mode state for the editor. Separate from
// EditorState because these are UI controls, not document changes — they
// don't participate in undo/redo and never modify the JSON.

export type ToolMode = 'control' | 'select' | 'translate' | 'rotate' | 'scale';
export type GizmoSpace = 'world' | 'local';

export interface SnapConfig {
  enabled: boolean;
  translate: number;
  rotateDeg: number;
  scale: number;
}

export interface AxisLock { x: boolean; y: boolean; z: boolean }

export interface EditorToolData {
  mode: ToolMode;
  space: GizmoSpace;
  snap: SnapConfig;
  axisLock: AxisLock;
  showGrid: boolean;
  showAxes: boolean;
  showStats: boolean;
}

const DEFAULTS: EditorToolData = {
  // Default to 'control' so opening the editor lands the user in fly-cam
  // navigation. Switching to translate/rotate/scale freezes the camera so
  // gizmo drags don't fight pointer-lock.
  mode: 'control',
  space: 'world',
  snap: { enabled: false, translate: 1, rotateDeg: 15, scale: 0.1 },
  axisLock: { x: true, y: true, z: true },
  showGrid: true,
  showAxes: true,
  showStats: false
};

type Listener = (s: EditorToolData) => void;

export class EditorTool {
  private data: EditorToolData = { ...DEFAULTS, snap: { ...DEFAULTS.snap }, axisLock: { ...DEFAULTS.axisLock } };
  private listeners: Listener[] = [];

  get(): EditorToolData { return this.data; }

  subscribe(fn: Listener): () => void {
    this.listeners.push(fn);
    return () => {
      const i = this.listeners.indexOf(fn);
      if (i >= 0) this.listeners.splice(i, 1);
    };
  }

  setMode(mode: ToolMode): void {
    if (this.data.mode === mode) return;
    this.data = { ...this.data, mode };
    this.notify();
  }
  setSpace(space: GizmoSpace): void {
    if (this.data.space === space) return;
    this.data = { ...this.data, space };
    this.notify();
  }
  setSnap(next: Partial<SnapConfig>): void {
    this.data = { ...this.data, snap: { ...this.data.snap, ...next } };
    this.notify();
  }
  setAxisLock(next: Partial<AxisLock>): void {
    this.data = { ...this.data, axisLock: { ...this.data.axisLock, ...next } };
    this.notify();
  }
  setShowGrid(v: boolean): void { this.data = { ...this.data, showGrid: v }; this.notify(); }
  setShowAxes(v: boolean): void { this.data = { ...this.data, showAxes: v }; this.notify(); }
  setShowStats(v: boolean): void { this.data = { ...this.data, showStats: v }; this.notify(); }

  private notify(): void {
    for (const fn of this.listeners.slice()) fn(this.data);
  }
}
